import { Bucket } from './types';

/*
 * Date labels for the grid, month-first for US districts. ISO dates are parsed
 * as local midnight so the day never shifts with the viewer's timezone. Weeks
 * run Monday to Sunday, as the server snaps them.
 */

const EN_DASH = '–';

const parseIsoDate = (iso: string): Date => new Date(`${iso}T00:00:00`);

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const shortMonth = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'short' });

const monthDay = (date: Date): string => `${shortMonth(date)} ${date.getDate()}`;

/**
 * Column header text: "Jan 20–26" for a week, "Jan 27–Feb 2" when the week
 * crosses a month, "Dec 29–Jan 4" across a year, and "Jan 2026" for a month.
 */
export const formatBucketHeader = (iso: string, bucket: Bucket): string => {
  const start = parseIsoDate(iso);
  if (bucket === 'month') {
    return start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  const end = addDays(start, 6);
  const endLabel =
    end.getMonth() === start.getMonth() ? String(end.getDate()) : monthDay(end);
  return `${monthDay(start)}${EN_DASH}${endLabel}`;
};

/**
 * The long form, for cell descriptions and tooltips: "week of Mar 2–8, 2026",
 * "week of Dec 29, 2025–Jan 4, 2026" across a year, and "January 2026" for a
 * month (a whole month, not a single date).
 */
export const formatBucket = (iso: string, bucket: Bucket): string => {
  const start = parseIsoDate(iso);
  if (bucket === 'month') {
    return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  const end = addDays(start, 6);
  if (end.getFullYear() !== start.getFullYear()) {
    return `week of ${monthDay(start)}, ${start.getFullYear()}${EN_DASH}${monthDay(
      end,
    )}, ${end.getFullYear()}`;
  }
  return `week of ${formatBucketHeader(iso, 'week')}, ${start.getFullYear()}`;
};

/** A teacher's last sign-in: "Mar 6, 2026", or "never". */
export const formatLastSeen = (iso: string | null): string => {
  if (!iso) {
    return 'never';
  }
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};
